import apiClient from '@/lib/api/apiClient'
import type { PmsAuditPage, PmsAuditRow } from '@/features/pms-automation'
import type {
  MatchingAttempt,
  MatchingAutomationMode,
  MatchingListPage,
  MatchingListParams,
  MatchingPropertyOverview,
  MatchingPropertyRow,
  MatchingResidentRow,
  MatchingRun,
} from '../types'

function unwrap<T>(raw: unknown): T {
  let value = raw
  for (let depth = 0; depth < 4; depth += 1) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) break
    const object = value as Record<string, unknown>
    const nested = object.data ?? object.result ?? object.payload
    if (nested == null) break
    value = nested
  }
  return value as T
}

function unwrapPaged<T>(raw: unknown): MatchingListPage<T> {
  const value = unwrap<Record<string, unknown> | T[]>(raw)
  if (Array.isArray(value)) {
    return {
      items: value,
      current: 1,
      last: 1,
      total: value.length,
      perPage: value.length || 15,
    }
  }
  const items = (Array.isArray(value.items) ? value.items : []) as T[]
  return {
    items,
    current: Number(value.current || 1),
    last: Math.max(1, Number(value.last || 1)),
    total: Number(value.total ?? items.length),
    perPage: Number(value.perPage || 15),
  }
}

export async function fetchMatchingProperties(params?: MatchingListParams & {
  company_id?: string | number
  mode?: string
  search?: string
}): Promise<MatchingListPage<MatchingPropertyRow>> {
  const { data } = await apiClient.get('/api/pms-resident-matching/properties', { params })
  return unwrapPaged<MatchingPropertyRow>(data)
}

export async function updateMatchingPropertyMode(
  yardiPropertyId: number,
  matchingAutomationMode: MatchingAutomationMode,
): Promise<unknown> {
  const { data } = await apiClient.post('/api/pms-resident-matching/properties/mode', {
    yardi_property_id: yardiPropertyId,
    matching_automation_mode: matchingAutomationMode,
  })
  return unwrap(data)
}

export async function fetchMatchingOverview(propertyId: string | number): Promise<MatchingPropertyOverview> {
  const { data } = await apiClient.get(`/api/pms-resident-matching/properties/${propertyId}/overview`)
  return unwrap<MatchingPropertyOverview>(data)
}

export async function fetchMatchingResidents(
  propertyId: string | number,
  params?: MatchingListParams & { status?: string; phase?: string; search?: string },
): Promise<MatchingListPage<MatchingResidentRow>> {
  const { data } = await apiClient.get(`/api/pms-resident-matching/properties/${propertyId}/residents`, { params })
  return unwrapPaged<MatchingResidentRow>(data)
}

export async function fetchMatchingRuns(limit = 20): Promise<MatchingRun[]> {
  const { data } = await apiClient.get('/api/pms-resident-matching/runs', { params: { limit } })
  const rows = unwrap<MatchingRun[]>(data)
  return Array.isArray(rows) ? rows : []
}

export async function fetchMatchingRunDetail(runId: string | number): Promise<{
  run: MatchingRun
  attempts: MatchingAttempt[]
}> {
  const { data } = await apiClient.get(`/api/pms-resident-matching/runs/${runId}`)
  const result = unwrap<{ run: MatchingRun; attempts: MatchingAttempt[] }>(data)
  return {
    run: result.run,
    attempts: Array.isArray(result.attempts) ? result.attempts : [],
  }
}

export async function fetchResidentMatchingAudit(params?: {
  property_id?: string | number
  company_id?: string | number
  page?: number
  per_page?: number
}): Promise<PmsAuditPage> {
  const { data } = await apiClient.get('/api/pms-resident-matching/audit', { params })
  const result = unwrap<{
    rows?: PmsAuditRow[]
    total?: number
    page?: number
    per_page?: number
    last_page?: number
  } | PmsAuditRow[]>(data)
  if (Array.isArray(result)) {
    return {
      rows: result,
      total: result.length,
      page: 1,
      perPage: result.length || 15,
      lastPage: 1,
    }
  }
  return {
    rows: Array.isArray(result.rows) ? result.rows : [],
    total: Number(result.total || 0),
    page: Number(result.page || 1),
    perPage: Number(result.per_page || 15),
    lastPage: Number(result.last_page || 1),
  }
}

export async function saveResidentMatchingAuditNote(payload: {
  note: string
  property_id?: string | number
  company_id?: string | number
}): Promise<void> {
  await apiClient.post('/api/pms-resident-matching/audit', payload)
}

export async function triggerMatchingRun(payload?: {
  property_id?: string | number
  mode?: 'SHADOW' | 'ENABLED'
}): Promise<unknown> {
  const { data } = await apiClient.post('/api/pms-resident-matching/run', payload || { mode: 'SHADOW' })
  return unwrap(data)
}
